/* eslint-disable max-lines */
import React, { useEffect, useRef, useState, useLayoutEffect } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { useSelect, useMultipleSelection } from 'downshift';
import classNames from 'classnames';

import { SvgIcon } from 'common/react/components/SvgIcon';
import { getTranslation } from 'common/react/utils/translations';
import { DrawerBottom } from 'common/react/components/drawer';

/**
 * @param {object} props - Props.
 * @param {string} props.label - Label.
 * @param props.icon
 * @param {any[]} props.items - Items.
 * @param {any[]} props.selectedItems - Selected items.
 * @param {Function} props.getItemText - Given an item, get the text that
 * represents the item.
 * @param {Function} props.getItemLabel - Given an item, get the label for the
 * item. It can be text or some JSX.
 * @param {Function} props.getItemDisabled - Get disabled state for item if required.
 * @param {Function} props.onSelectedItemsChange - Fires when selectedItems changes
 * @returns {React.Element} Component.
 */

const isMobile = window.matchMedia(
    `(max-width: ${PULSE.app.measurements.desktop}px)`
);

const getSelectedItem = (itemsList, targetItem) => {
    for (let item of itemsList) {
        if (_.isEqual(item, targetItem)) {
            return item;
        }
    }
    return null;
};

const DROPDOWN_VERTICAL_OFFSET = 36;

export const SelectMultipleWithLimit = ({
    label,
    icon,
    items,
    getItemText,
    getItemLabel,
    getItemDisabled = null,
    onSelectedItemsChange,
    selections,
    addOnResetCallback,
    counterThreshold,
    maximumSelections,
    maximumSelectionsLabel,
    title
}) => {
    const [itemsSelected, setItemsSelected] = useState(selections);
    const [maxItemsSelected, setMaxItemsSelected] = useState(false);
    const [dropdownPosition, setDropdownPosition] = useState({
        top: 0,
        left: 0,
        width: 0,
        display: 'none'
    });

    const {
        getDropdownProps,
        addSelectedItem,
        removeSelectedItem,
        selectedItems,
        reset
    } = useMultipleSelection({
        selectedItems: itemsSelected,
        onSelectedItemsChange: ({ selectedItems: newSelectedItems }) => {
            /**
             * Check for custom index value selection, e.g. Header, maximum threshold message. If these are selected,
             * prevent these these values from being add to the 'newSelectedItems' array that's passed to the
             * 'onSelectedItemsChange' function. This ensures the correct count is generated for the counter display
             * and the maximum threshold calculation.
             */
            const customIndex = newSelectedItems.findIndex(
                (newSelItems) => newSelItems.type
            );
            customIndex !== -1 && newSelectedItems.splice(customIndex, 1);
            onSelectedItemsChange(newSelectedItems);

            setMaxItemsSelected(
                newSelectedItems.length >= parseInt(maximumSelections, 10)
            );

            selectRef?.current.dispatchEvent(
                new Event(
                    PULSE.app.common.CONSTANTS.EVENTS.INTERACTION.CHANGE,
                    { bubbles: true }
                )
            );
        },
        stateReducer: (state, actionAndChanges) => {
            const { type, changes } = actionAndChanges;

            switch (type) {
                case useMultipleSelection.stateChangeTypes
                    .FunctionAddSelectedItem:
                    return {
                        ...changes,
                        // De duplicate any selectedItems as it fires twice in some scenarios
                        selectedItems: [...new Set(changes.selectedItems)]
                    };
                default:
                    return changes;
            }
        }
    });

    const {
        isOpen,
        getToggleButtonProps,
        getMenuProps,
        highlightedIndex,
        getItemProps,
        getLabelProps,
        closeMenu
    } = useSelect({
        items,
        selectedItem: null,
        stateReducer: (state, actionAndChanges) => {
            const { type, changes } = actionAndChanges;

            const selectedItemObj = getSelectedItem(
                selectedItems,
                changes.selectedItem
            );

            switch (type) {
                case useSelect.stateChangeTypes.MenuKeyDownEnter:
                case useSelect.stateChangeTypes.ToggleButtonKeyDownEnter:
                case useSelect.stateChangeTypes.ToggleButtonKeyDownSpaceButton:
                case useSelect.stateChangeTypes.ItemClick: {
                    if (!changes.selectedItem) {
                        return changes;
                    }

                    // Check if the item is disabled
                    const isDisabled =
                        typeof getItemDisabled === 'function'
                            ? getItemDisabled(changes.selectedItem)
                            : false;
                    if (isDisabled) {
                        return {
                            ...changes,
                            selectedItem: null,
                            highlightedIndex: highlightedIndex,
                            isOpen: true
                        };
                    }

                    if (
                        selectedItems.some((selectedItem) =>
                            _.isEqual(selectedItem, changes.selectedItem)
                        )
                    ) {
                        removeSelectedItem(selectedItemObj);
                    } else if (!maxItemsSelected) {
                        addSelectedItem(changes.selectedItem);
                    }

                    return {
                        ...changes,
                        highlightedIndex: highlightedIndex,
                        isOpen: true
                    };
                }
                case useSelect.stateChangeTypes.ToggleButtonBlur:
                    return state;
                default:
                    return changes;
            }
        }
    });

    const selectRef = useRef();
    const optionsWrapper = useRef();
    const toggleButtonRef = useRef();
    const drawerBottomRef = useRef();
    const dropdownPortalWrapperRef = useRef();

    // Unchecks selected options after 'Clear' button click
    const clearClickHandler = () => {
        reset();
        setMaxItemsSelected(false);
        toggleButtonRef?.current.focus();
    };

    const onClickOutside = (evnt) => {
        const dropdownEl = optionsWrapper.current;
        const portalWrapperEl = dropdownPortalWrapperRef.current;
        if (
            !selectRef.current?.contains(evnt.target) &&
            !drawerBottomRef.current?.contains(evnt.target) &&
            !(dropdownEl && dropdownEl.contains(evnt.target)) &&
            !(portalWrapperEl && portalWrapperEl.contains(evnt.target))
        ) {
            closeMenu();
        }
    };

    /**
     * Hookup reset method to the list of reset functions to execute when the
     * reset button is clicked.
     */
    useEffect(() => {
        addOnResetCallback(reset);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setItemsSelected(selections);
    }, [selections]);

    useEffect(() => {
        document.addEventListener('click', onClickOutside);

        return () => {
            document.removeEventListener('click', onClickOutside);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useLayoutEffect(() => {
        if (isOpen && !isMobile.matches && toggleButtonRef.current) {
            const rect = toggleButtonRef.current.getBoundingClientRect();
            setDropdownPosition({
                top: rect.bottom + window.scrollY - DROPDOWN_VERTICAL_OFFSET,
                left: rect.left + window.scrollX,
                width: rect.width,
                display: 'block'
            });
        } else {
            setDropdownPosition((pos) => ({ ...pos, display: 'none' }));
        }
    }, [isOpen]);

    const dropdownItems = (item, index) => {
        const itemText = getItemLabel(item);

        const isSelected = selectedItems.some((selectedItem) =>
            _.isEqual(selectedItem, item)
        );

        const isDisabled =
            typeof getItemDisabled === 'function'
                ? getItemDisabled(item)
                : false;

        // Checks if highlighted item has already been selected
        const highlightedItemSelected = selectedItems.some(
            (selectedItem) => selectedItem.id === item.id
        );

        /**
         * Handles the class names assigned to the individual items in the list.
         * Some additional logic is applied to handle custom items, e.g. header
         * and threshold messaging. These should behave differently to other
         * selectable elements, i.e. shouldn't have a highlighted effect.
         */
        const className = classNames('select-multiple__option', {
            'select-multiple__option--selected': isSelected,
            'select-multiple__option--highlighted':
                highlightedIndex === index && !item.type,
            'select-multiple__option--disabled':
                (maxItemsSelected && !isSelected && !item.type) || isDisabled,
            'select-multiple__option--disabled-highlighted':
                maxItemsSelected &&
                highlightedItemSelected &&
                highlightedIndex === index &&
                !item.type &&
                isDisabled,
            'select-multiple__option--custom': item.type
        });

        const svgIcon = isSelected ? (
            <SvgIcon
                className="select-multiple__checkmark"
                icon="checkmark-square"
            />
        ) : (
            <SvgIcon
                className="select-multiple__checkmark-empty"
                icon="checkmark-square-empty"
            />
        );

        return (
            <li
                key={`${itemText}${index}`}
                {...getItemProps({
                    item,
                    index
                })}
                className={className}
            >
                {svgIcon}
                <span className="select-multiple__option-label-wrapper">
                    {getItemLabel(item)}
                </span>
            </li>
        );
    };

    const selectedItemsLabelsReducer = (acc, item) => {
        return acc ? acc + ', ' + getItemText(item) : acc + getItemText(item);
    };

    const toggleButtonLabelText = () => {
        if (counterThreshold && counterThreshold <= selectedItems?.length) {
            return (
                <p className="counter-wrapper">
                    <span className="select__current-text">{title}</span>
                    <span className="counter counter--large">
                        {selectedItems?.length}
                    </span>
                </p>
            );
        }

        if (
            selectedItems?.length > 0 &&
            selectedItems?.length < counterThreshold
        ) {
            return selectedItems?.reduce(selectedItemsLabelsReducer, '');
        }

        return null;
    };

    const toggleButtonLabel = () => {
        return selectedItems?.length === 0 ? label : null;
    };

    const toggleButtonCurrentElement = (
        <div className="select-multiple__current">
            {icon && <SvgIcon className="select-multiple__icon" icon={icon} />}
            {toggleButtonLabel()}
            {toggleButtonLabelText()}
        </div>
    );

    const onElementBlur = (evt) => {
        const element = evt?.relatedTarget || evt?.target;
        if (
            !selectRef.current?.contains(element) &&
            !element?.classList.contains('rc-drawer')
        ) {
            closeMenu();
        }
    };

    useEffect(() => {
        if (selectedItems.length === 0 && maxItemsSelected) {
            setMaxItemsSelected(false);
        }
    }, [selectedItems]);

    return (
        <div className="w-100" ref={selectRef}>
            <div
                className={classNames('select-multiple', {
                    'select-multiple--open': isOpen
                })}
            >
                <div
                    className={classNames('select-multiple__display-wrapper', {
                        'select-multiple__display-wrapper--has-selected-items':
                            !!selectedItems.length
                    })}
                >
                    <button
                        type="button"
                        aria-label={label}
                        {...getToggleButtonProps(
                            getDropdownProps({ ref: toggleButtonRef })
                        )}
                        className="select-multiple__display"
                    >
                        {toggleButtonCurrentElement}

                        <SvgIcon
                            icon="chevron-down"
                            className={classNames('select-multiple__chevron', {
                                'select-multiple__chevron--open': isOpen
                            })}
                        />
                    </button>
                </div>
            </div>

            {!isMobile.matches
                ? ReactDOM.createPortal(
                      <div
                          ref={dropdownPortalWrapperRef}
                          style={{
                              position: 'absolute',
                              top: dropdownPosition.top,
                              left: dropdownPosition.left,
                              width: dropdownPosition.width,
                              zIndex: 80,
                              display: dropdownPosition.display
                          }}
                      >
                          <div className="select-multiple__options-wrapper">
                              {isOpen ? (
                                  <>
                                      <div
                                          className={`select-multiple__header`}
                                      >
                                          {title}
                                          {selectedItems?.length > 0 ? (
                                              <button
                                                  type="button"
                                                  className="select-multiple__header-clear button-new button-new--secondary"
                                                  tabIndex={0}
                                                  onClick={clearClickHandler}
                                                  onBlur={onElementBlur}
                                              >
                                                  {getTranslation(
                                                      'label.clear'
                                                  )}
                                              </button>
                                          ) : null}
                                      </div>
                                      {maximumSelectionsLabel ? (
                                          <div
                                              className={`select-multiple__max-threshold select-multiple__option--disabled ${
                                                  !maxItemsSelected && 'u-hide'
                                              }`}
                                          >
                                              {maximumSelectionsLabel}
                                          </div>
                                      ) : null}
                                  </>
                              ) : null}
                              <ul
                                  {...getMenuProps(
                                      {},
                                      { suppressRefError: true }
                                  )}
                                  className={classNames(
                                      'select-multiple__options-list',
                                      {
                                          'u-hide': !isOpen
                                      }
                                  )}
                                  ref={optionsWrapper}
                                  onBlur={onElementBlur}
                              >
                                  {isOpen && items.map(dropdownItems)}
                              </ul>
                          </div>
                      </div>,
                      document.body
                  )
                : null}

            {isMobile.matches ? (
                <DrawerBottom
                    isOpen={isOpen}
                    onClose={() => closeMenu()}
                    heading={label}
                >
                    <div
                        className="select-multiple select-multiple--mobile select-multiple--open"
                        ref={drawerBottomRef}
                    >
                        {/*
                        An invisible label for accessibility purposes.
                    */}
                        <label className="u-hide" {...getLabelProps()}>
                            {label}
                        </label>

                        {/*
                        An invisible dummy toggle button just so we're calling
                        getToggleButtonProps to ensure rc-drawer doesn't crash.
                    */}
                        <button
                            className="u-hide"
                            {...getToggleButtonProps(getDropdownProps())}
                        ></button>

                        <div className="select-multiple__options-wrapper">
                            {selectedItems.length === maximumSelections ? (
                                <p className="select-multiple__max-threshold">
                                    {maximumSelectionsLabel}
                                </p>
                            ) : null}
                            <ul
                                {...getMenuProps(
                                    {},
                                    { suppressRefError: true }
                                )}
                                className={classNames(
                                    'select-multiple__options-list',
                                    {
                                        'u-hide': !isOpen
                                    }
                                )}
                                ref={optionsWrapper}
                            >
                                {items.map((item, index) => {
                                    const itemText = getItemLabel(item);
                                    const isSelected = selectedItems.some(
                                        (selectedItem) =>
                                            _.isEqual(selectedItem, item)
                                    );
                                    /* eslint-disable-next-line */
                                    const isDisabled =
                                        typeof getItemDisabled === 'function'
                                            ? getItemDisabled(item)
                                            : false;

                                    return (
                                        !item.type && (
                                            <li
                                                key={`${itemText}${index}`}
                                                {...getItemProps({
                                                    item,
                                                    index
                                                })}
                                                className={classNames(
                                                    'select-multiple__option',
                                                    {
                                                        'select-multiple__option--selected':
                                                            isSelected,
                                                        'select-multiple__option--highlighted':
                                                            highlightedIndex ===
                                                            index,
                                                        'select-multiple__option--disabled':
                                                            (maxItemsSelected &&
                                                                !isSelected) ||
                                                            isDisabled
                                                    }
                                                )}
                                            >
                                                {isSelected ? (
                                                    <SvgIcon
                                                        className="select-multiple__checkmark"
                                                        icon="checkmark-square"
                                                    />
                                                ) : (
                                                    <SvgIcon
                                                        className="select-multiple__checkmark-empty"
                                                        icon="checkmark-square-empty"
                                                    />
                                                )}

                                                <span className="select-multiple__option-label-wrapper">
                                                    {getItemLabel(item)}
                                                </span>
                                            </li>
                                        )
                                    );
                                })}
                            </ul>
                            {isOpen && selectedItems.length > 0 ? (
                                <div className="select-multiple__header">
                                    <button
                                        type="button"
                                        className="select-multiple__header-clear button-new button-new--secondary u-margin-left-auto"
                                        onClick={() => clearClickHandler()}
                                    >
                                        {getTranslation('label.clear')}
                                    </button>
                                </div>
                            ) : null}
                        </div>
                    </div>
                </DrawerBottom>
            ) : null}
        </div>
    );
};

SelectMultipleWithLimit.propTypes = {
    label: PropTypes.string.isRequired,
    icon: PropTypes.string,
    items: PropTypes.arrayOf(PropTypes.any),
    itemToString: PropTypes.func,
    selections: PropTypes.array,
    onSelectedItemsChange: PropTypes.func.isRequired,
    addOnResetCallback: PropTypes.func.isRequired,
    getItemText: PropTypes.func.isRequired,
    getItemLabel: PropTypes.func.isRequired,
    getItemDisabled: PropTypes.func,
    counterThreshold: PropTypes.number,
    isOpen: PropTypes.bool,
    title: PropTypes.string,
    maximumSelections: PropTypes.number,
    maximumSelectionsLabel: PropTypes.string
};
